"use client";

import clsx from "clsx";
import Image, { type StaticImageData } from "next/image";
import Link from "next/link";
import { useState } from "react";

type ImageProps = {
  src: string | StaticImageData;
  alt?: string;
};

export type ServicesDetailClientLink = {
  url: string;
  heading: string;
  tag: string;
  image: ImageProps;
  _sanityKey?: string;
};

type ServicesDetailClientProps = {
  hoverLinks: ServicesDetailClientLink[];
};

export function ServicesDetailClient({ hoverLinks }: ServicesDetailClientProps) {
  const [hoveredIndex, setHoveredIndex] = useState<null | number>(null);
  const activeIndex = hoveredIndex ?? 0;
  const activeLink = hoverLinks[activeIndex];

  return (
    <section
      id="uslugi-szczegoly"
      data-no-reveal-scope
      className="px-[3%] pt-16 pb-28 text-on-primary md:pt-24 md:pb-36 lg:pt-28 lg:pb-44"
    >
      <div className="container">
        <div className="grid grid-cols-1 gap-x-10 lg:grid-cols-2">
          <div
            className="min-w-0"
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {hoverLinks.map((link, index) => (
              <div key={link._sanityKey ?? `${link.heading}-${index}`}>
                <Link
                  href={link.url}
                  className="group relative z-10 flex flex-col flex-wrap items-start justify-start gap-4 border-b border-white/15 py-5 no-underline transition-all duration-300 sm:flex-row sm:items-center md:gap-8 md:py-6"
                  onMouseEnter={() => setHoveredIndex(index)}
                >
                  <div className="flex w-full flex-wrap items-center justify-between gap-4">
                    <h3
                      className={clsx(
                        "text-lg text-on-primary transition-colors duration-300 md:text-xl md:leading-[1.3]",
                        {
                          "text-on-primary/70":
                            hoveredIndex !== index && hoveredIndex !== null,
                          "text-on-primary":
                            hoveredIndex === index || hoveredIndex === null,
                        },
                      )}
                    >
                      {link.heading}
                    </h3>
                    <span
                      className={clsx(
                        "hidden items-center text-xs uppercase tracking-wider text-on-primary/80 transition-all duration-300 md:inline-flex",
                        {
                          "translate-x-0 opacity-100": hoveredIndex === index,
                          "translate-x-1 opacity-0": hoveredIndex !== index,
                        },
                      )}
                    >
                      zobacz
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Kolumna rozciąga się na wysokość listy — wtedy sticky ma tor jazdy. */}
          <div className="relative hidden lg:block">
            <div className="sticky top-24">
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                {activeLink ? (
                  <Image
                    className="object-cover"
                    src={activeLink.image.src}
                    alt={activeLink.image.alt ?? activeLink.heading}
                    fill
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    quality={80}
                    priority={activeIndex === 0}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
